import React, {
  forwardRef,
  ReactNode,
  useMemo,
} from 'react';
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  TextFieldProps,
} from '@mui/material';

import { useDebouncedState } from '@shared/hooks/useDebouncedState';
import { useDictionary } from '@cvt/hooks/useDictionary';

import { useStoryTagsQuery } from '../hooks/useStoryTagsQuery';


export type StoryTagAutocompleteProps = Omit<
  AutocompleteProps<string, true, true, true>,
  'value' | 'onChange' | 'options' | 'renderInput' | 'inputValue' | 'onInputChange' | 'loading' | 'renderTags'
> & {
  value: string[];
  onChange: (tags: string[]) => void;
  label?: string;
  error?: boolean;
  helperText?: ReactNode;
  TextFieldProps?: TextFieldProps;
};

export const StoryTagAutocomplete = forwardRef<HTMLInputElement, StoryTagAutocompleteProps>(({
  value,
  onChange,
  label,
  error,
  helperText,
  TextFieldProps,
  freeSolo,
  ...props
}, ref) => {
  const [search, setSearch, debouncedSearch, isDebouncing] = useDebouncedState('');
  const { tags = [], isLoading } = useStoryTagsQuery({ search: debouncedSearch });
  const dictionary = useDictionary();

  const options = useMemo(() => {
    return (isDebouncing || isLoading) ? [] : tags.map(tag => tag.name);
  }, [tags, isDebouncing, isLoading]);

  return (
    <Box width={(props.sx as any)?.width || '100%'}>
      <FormControl fullWidth error={error}>
        {!!label && <InputLabel shrink>{label}</InputLabel>}
        <Autocomplete
          multiple
          freeSolo={freeSolo}
          filterSelectedOptions
          options={options}
          value={value}
          onChange={(_, newValue) => onChange(newValue)}
          inputValue={search}
          onInputChange={(_, newInputValue) => setSearch(newInputValue)}
          loading={isDebouncing || (!!debouncedSearch && isLoading)}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  variant="outlined"
                  label={option}
                  {...tagProps}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              {...TextFieldProps}
              inputRef={ref}
              label={label}
              variant="outlined"
              error={error}
              helperText={helperText}
              placeholder={TextFieldProps?.placeholder || dictionary.forms.fieldTagsPlaceholder}
              InputProps={{
                ...params.InputProps,
                ...TextFieldProps?.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress size={20}/> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                ...TextFieldProps?.InputLabelProps,
              }}
            />
          )}
          {...props}
        />
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
});
